import { Checkbox, Form, Input, Radio, Typography } from 'antd';
import type { ReactNode } from 'react';

const { Text } = Typography;

export type YesNo = 'yes' | 'no';
export type YesNoUnknown = 'yes' | 'no' | 'unknown';

export const SUN_EXPOSURE_OPTIONS = [
  { value: 'under_30_min', label: '< 30 min' },
  { value: '30_to_60_min', label: '30 min – 1 oră' },
  { value: '1_to_3_hours', label: '1–3 ore' },
  { value: 'over_3_hours', label: '> 3 ore' },
] as const;

export const SKIN_TYPE_OPTIONS = [
  { value: 'TYPE_I', label: 'Tip I — piele foarte albă, se arde întotdeauna, nu se bronzează' },
  { value: 'TYPE_II', label: 'Tip II — piele deschisă, se arde ușor, se bronzează slab' },
  { value: 'TYPE_III', label: 'Tip III — piele albă mai închisă, se bronzează după o arsură inițială' },
  { value: 'TYPE_IV', label: 'Tip IV — piele brun deschis, se arde minim, se bronzează ușor' },
  { value: 'TYPE_V', label: 'Tip V — piele brună, se arde rar, se bronzează intens' },
  { value: 'TYPE_VI', label: 'Tip VI — piele brun închis / neagră, nu se arde niciodată' },
] as const;

export const SKIN_CHANGES_OPTIONS = [
  { value: 'new_moles', label: 'Alunițe noi' },
  { value: 'color_changes', label: 'Modificări de culoare' },
  { value: 'growth', label: 'Creștere în dimensiuni' },
  { value: 'bleeding', label: 'Sângerare' },
  { value: 'itching', label: 'Mâncărime' },
  { value: 'ulcerations', label: 'Ulcerații persistente' },
] as const;

export const SELF_EXAM_OPTIONS = [
  { value: 'monthly', label: 'Lunar' },
  { value: 'six_months', label: 'La 6 luni' },
  { value: 'yearly', label: 'Anual' },
  { value: 'never', label: 'Niciodată' },
] as const;

export const PREVIOUS_SKIN_CANCER_TYPE_OPTIONS = [
  { value: 'melanoma', label: 'Melanom' },
  { value: 'basal_cell_carcinoma', label: 'Carcinom bazocelular' },
  { value: 'squamous_cell_carcinoma', label: 'Carcinom scuamocelular' },
  { value: 'other', label: 'Altul' },
] as const;

export const SKIN_CANCER_TREATMENT_OPTIONS = [
  { value: 'excision_surgery', label: 'Excizie chirurgicală' },
  { value: 'radiotherapy', label: 'Terapie cu radiații' },
  { value: 'chemotherapy', label: 'Chimioterapie' },
  { value: 'immunotherapy', label: 'Imunoterapie' },
  { value: 'hedgehog_inhibitors', label: 'Inhibitori Hedgehog' },
  { value: 'other_treatment', label: 'Alte tratamente specifice' },
] as const;

export const OTHER_CANCER_TYPE_OPTIONS = [
  { value: 'breast', label: 'Sân' },
  { value: 'prostate', label: 'Prostată' },
  { value: 'pancreas', label: 'Pancreas' },
  { value: 'colon', label: 'Colon' },
  { value: 'other_cancer_type', label: 'Altul' },
] as const;

export const GENETIC_MUTATION_OPTIONS = [
  { value: 'brca', label: 'BRCA' },
  { value: 'cdkn2a', label: 'CDKN2A' },
  { value: 'bap1', label: 'BAP1' },
  { value: 'mc1r', label: 'MC1R' },
  { value: 'other', label: 'Altul' },
] as const;

export const CHRONIC_SKIN_CONDITION_OPTIONS = [
  { value: 'psoriasis', label: 'Psoriazis' },
  { value: 'eczema', label: 'Eczeme / dermatită' },
  { value: 'vitiligo', label: 'Vitiligo' },
  { value: 'rosacea', label: 'Rozacee' },
  { value: 'acne', label: 'Acnee' },
  { value: 'other', label: 'Altul' },
] as const;

export const PHOTOSENSITIZING_MEDICATION_OPTIONS = [
  { value: 'antibiotics', label: 'Antibiotice' },
  { value: 'diuretics', label: 'Diuretice' },
  { value: 'retinoids', label: 'Retinoizi' },
  { value: 'nsaids', label: 'AINS' },
  { value: 'antidepressants', label: 'Antidepresive' },
  { value: 'antifungals', label: 'Antifungice' },
  { value: 'other', label: 'Altul' },
] as const;

export const TOXIC_CHEMICAL_OPTIONS = [
  { value: 'arsenic', label: 'Arsenic' },
  { value: 'hydrocarbons', label: 'Hidrocarburi' },
  { value: 'tar', label: 'Gudron' },
  { value: 'nickel', label: 'Nichel' },
  { value: 'chromium', label: 'Crom' },
  { value: 'other', label: 'Altul' },
] as const;

export const TANNING_PRODUCT_OPTIONS = [
  { value: 'tanning_oils', label: 'Uleiuri pentru plajă' },
  { value: 'lotions_without_spf', label: 'Creme / loțiuni fără SPF' },
  { value: 'self_tanners', label: 'Autobronzante' },
  { value: 'other', label: 'Altul' },
] as const;

/** Câte episoade de arsuri solare severe (alegere unică + „Altul”). */
export const SUNBURN_EPISODE_OPTIONS = [
  { value: 'one', label: '1' },
  { value: 'two_to_three', label: '2–3' },
  { value: 'four_to_five', label: '4–5' },
  { value: 'over_five', label: 'Peste 5' },
  { value: 'other', label: 'Altul' },
] as const;

export const SPF_OPTIONS = [
  { value: 'spf_15', label: 'SPF 15' },
  { value: 'spf_30', label: 'SPF 30' },
  { value: 'spf_50', label: 'SPF 50' },
  { value: 'spf_50_plus', label: 'SPF 50+' },
  { value: 'other', label: 'Altul' },
] as const;

export const TANNING_SALON_FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Săptămânal' },
  { value: 'monthly', label: 'Lunar' },
  { value: 'occasionally', label: 'Ocazional' },
  { value: 'other', label: 'Altul' },
] as const;

export const HIGH_UV_ZONE_OPTIONS = [
  { value: 'tropics', label: 'Tropice' },
  { value: 'high_mountains', label: 'Munți înalți' },
  { value: 'near_equator', label: 'Aproape de ecuator' },
  { value: 'other', label: 'Altul' },
] as const;

export const OCCUPATIONAL_EXPOSURE_OPTIONS = [
  { value: 'chemicals_solvents', label: 'Chimicale / solvenți' },
  { value: 'pesticides', label: 'Pesticide' },
  { value: 'uv_radiation', label: 'Radiații UV' },
  { value: 'dust_fumes', label: 'Praf / gaze industriale' },
  { value: 'heavy_metals', label: 'Metale grele' },
  { value: 'other', label: 'Altul' },
] as const;

/** Modificări de piele — „Altul” deschide câmpul liber. */
export const SKIN_CHANGES_WITH_OTHER_OPTIONS = [
  ...SKIN_CHANGES_OPTIONS,
  { value: 'other', label: 'Altul' },
] as const;

export const SKIN_CHANGES_KEYS = SKIN_CHANGES_OPTIONS.map((o) => o.value);
export const PREVIOUS_SKIN_CANCER_TYPE_KEYS = PREVIOUS_SKIN_CANCER_TYPE_OPTIONS.map((o) => o.value);
export const SKIN_CANCER_TREATMENT_KEYS = SKIN_CANCER_TREATMENT_OPTIONS.map((o) => o.value);
export const OTHER_CANCER_TYPE_KEYS = OTHER_CANCER_TYPE_OPTIONS.map((o) => o.value);
export const GENETIC_MUTATION_KEYS = GENETIC_MUTATION_OPTIONS.map((o) => o.value);
export const CHRONIC_SKIN_CONDITION_KEYS = CHRONIC_SKIN_CONDITION_OPTIONS.map((o) => o.value);
export const PHOTOSENSITIZING_MEDICATION_KEYS = PHOTOSENSITIZING_MEDICATION_OPTIONS.map((o) => o.value);
export const TOXIC_CHEMICAL_KEYS = TOXIC_CHEMICAL_OPTIONS.map((o) => o.value);
export const TANNING_PRODUCT_KEYS = TANNING_PRODUCT_OPTIONS.map((o) => o.value);
export const SUNBURN_EPISODE_KEYS = SUNBURN_EPISODE_OPTIONS.map((o) => o.value);
export const SPF_KEYS = SPF_OPTIONS.map((o) => o.value);
export const TANNING_SALON_FREQUENCY_KEYS = TANNING_SALON_FREQUENCY_OPTIONS.map((o) => o.value);
export const HIGH_UV_ZONE_KEYS = HIGH_UV_ZONE_OPTIONS.map((o) => o.value);
export const OCCUPATIONAL_EXPOSURE_KEYS = OCCUPATIONAL_EXPOSURE_OPTIONS.map((o) => o.value);

/** Valoarea opțiunii „Altul / Alt tip / Alte tratamente” care deschide câmpul liber. */
export const OTHER_OPTION_VALUE = 'other';
export const OTHER_TREATMENT_OPTION_VALUE = 'other_treatment';
export const OTHER_CANCER_OPTION_VALUE = 'other_cancer_type';

interface YesNoFieldProps {
  label: string;
  value?: YesNo;
  required?: boolean;
  onChange: (value: YesNo) => void;
}

/** Întrebare simplă Da/Nu (fără „Dacă da, specificați”). */
export const YesNoField = ({ label, value, required, onChange }: YesNoFieldProps) => (
  <Form.Item label={label} required={required} style={{ marginBottom: 16 }}>
    <Radio.Group value={value} onChange={(e) => onChange(e.target.value)}>
      <Radio value="yes">Da</Radio>
      <Radio value="no">Nu</Radio>
    </Radio.Group>
  </Form.Item>
);

interface YesNoDetailProps {
  label: string;
  value?: YesNo;
  detailValue?: string;
  detailLabel?: string;
  detailPlaceholder?: string;
  detailRows?: number;
  required?: boolean;
  onChange: (value: YesNo) => void;
  onDetailChange: (value: string) => void;
}

/** Întrebare Da/Nu + câmpul liber „Dacă da, specificați…” (ascuns la Nu). */
export const YesNoDetailField = ({
  label,
  value,
  detailValue = '',
  detailLabel = 'Dacă da, specificați',
  detailPlaceholder,
  detailRows = 2,
  required,
  onChange,
  onDetailChange,
}: YesNoDetailProps) => (
  <div className="anamnesis-q" data-testid={`anamnesis-q-${label.slice(0, 40)}`}>
    <Form.Item label={label} required={required} style={{ marginBottom: 8 }}>
      <Radio.Group
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (e.target.value !== 'yes' && detailValue) onDetailChange('');
        }}
      >
        <Radio value="yes">Da</Radio>
        <Radio value="no">Nu</Radio>
      </Radio.Group>
    </Form.Item>
    {value === 'yes' && (
      <Form.Item label={detailLabel} style={{ marginBottom: 16, marginLeft: 12 }}>
        <Input.TextArea
          rows={detailRows}
          value={detailValue}
          placeholder={detailPlaceholder}
          onChange={(e) => onDetailChange(e.target.value)}
        />
      </Form.Item>
    )}
  </div>
);

interface YesNoUnknownFieldProps {
  label: string;
  value?: YesNoUnknown;
  required?: boolean;
  onChange: (value: YesNoUnknown) => void;
}

export const YesNoUnknownField = ({ label, value, required, onChange }: YesNoUnknownFieldProps) => (
  <Form.Item label={label} required={required} style={{ marginBottom: 16 }}>
    <Radio.Group value={value} onChange={(e) => onChange(e.target.value)}>
      <Radio value="yes">Da</Radio>
      <Radio value="no">Nu</Radio>
      <Radio value="unknown">Nu știu</Radio>
    </Radio.Group>
  </Form.Item>
);

interface ChoiceFieldProps<T extends string> {
  label: string;
  value?: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  required?: boolean;
  onChange: (value: T) => void;
  children?: ReactNode;
}

export function ChoiceField<T extends string>({
  label,
  value,
  options,
  required,
  onChange,
  children,
}: ChoiceFieldProps<T>) {
  return (
    <Form.Item label={label} required={required} style={{ marginBottom: 16 }}>
      <Radio.Group
        value={value}
        onChange={(e) => onChange(e.target.value)}
        options={options.map((o) => ({ value: o.value, label: o.label }))}
        style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
      />
      {children}
    </Form.Item>
  );
}

interface MultiCheckFieldProps {
  label: string;
  value: string[];
  options: ReadonlyArray<{ value: string; label: string }>;
  onChange: (value: string[]) => void;
}

export const MultiCheckField = ({ label, value, options, onChange }: MultiCheckFieldProps) => (
  <Form.Item label={label} style={{ marginBottom: 16 }}>
    <Checkbox.Group
      value={value}
      onChange={(v) => onChange(v as string[])}
      options={options.map((o) => ({ value: o.value, label: o.label }))}
      className="anamnesis-checks"
    />
  </Form.Item>
);

interface MultiCheckWithOtherFieldProps {
  label: string;
  value: string[];
  options: ReadonlyArray<{ value: string; label: string }>;
  /** Valoarea opțiunii care deschide câmpul liber (implicit „other”). */
  otherOptionValue?: string;
  otherLabel?: string;
  otherValue: string;
  otherPlaceholder?: string;
  onChange: (value: string[]) => void;
  onOtherChange: (value: string) => void;
}

/**
 * Listă de opțiuni (checkbox) + câmp liber „Care…?” vizibil doar când
 * opțiunea „Altul / Alt tip…” este bifată. La debifare, câmpul liber se golește.
 */
export const MultiCheckWithOtherField = ({
  label,
  value,
  options,
  otherOptionValue = OTHER_OPTION_VALUE,
  otherLabel = 'Care altul',
  otherValue,
  otherPlaceholder,
  onChange,
  onOtherChange,
}: MultiCheckWithOtherFieldProps) => {
  const otherChecked = value.includes(otherOptionValue);
  return (
    <div className="anamnesis-q">
      <MultiCheckField
        label={label}
        value={value}
        options={options}
        onChange={(next) => {
          onChange(next);
          if (!next.includes(otherOptionValue) && otherValue) onOtherChange('');
        }}
      />
      {otherChecked && (
        <Form.Item label={otherLabel} style={{ marginBottom: 16, marginLeft: 12 }}>
          <Input.TextArea
            rows={2}
            value={otherValue}
            placeholder={otherPlaceholder}
            onChange={(e) => onOtherChange(e.target.value)}
          />
        </Form.Item>
      )}
    </div>
  );
};

interface YesNoOptionsWithOtherFieldProps {
  label: string;
  value?: YesNo;
  options: ReadonlyArray<{ value: string; label: string }>;
  selected: string[];
  otherOptionValue?: string;
  otherLabel?: string;
  otherValue: string;
  otherPlaceholder?: string;
  required?: boolean;
  /** Bloc opțional sub listă (ex: „rudele afectate”) — tot la Da. */
  children?: ReactNode;
  onChange: (value: YesNo) => void;
  onOptionsChange: (value: string[]) => void;
  onOtherChange: (value: string) => void;
}

/**
 * Pattern complet: Da/Nu → la Da listă de opțiuni → „Altul” deschide textul liber.
 * La Nu: se ascund și se golesc opțiunile + textul liber.
 */
export const YesNoOptionsWithOtherField = ({
  label,
  value,
  options,
  selected,
  otherOptionValue = OTHER_OPTION_VALUE,
  otherLabel = 'Care altul',
  otherValue,
  otherPlaceholder,
  required,
  children,
  onChange,
  onOptionsChange,
  onOtherChange,
}: YesNoOptionsWithOtherFieldProps) => (
  <div className="anamnesis-q" data-testid={`anamnesis-q-${label.slice(0, 40)}`}>
    <Form.Item label={label} required={required} style={{ marginBottom: 8 }}>
      <Radio.Group
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (e.target.value !== 'yes') {
            if (selected.length) onOptionsChange([]);
            if (otherValue) onOtherChange('');
          }
        }}
      >
        <Radio value="yes">Da</Radio>
        <Radio value="no">Nu</Radio>
      </Radio.Group>
    </Form.Item>
    {value === 'yes' && (
      <>
        <MultiCheckWithOtherField
          label="Dacă da, specificați"
          value={selected}
          options={options}
          otherOptionValue={otherOptionValue}
          otherLabel={otherLabel}
          otherValue={otherValue}
          otherPlaceholder={otherPlaceholder}
          onChange={onOptionsChange}
          onOtherChange={onOtherChange}
        />
        {children}
      </>
    )}
  </div>
);

interface ChoiceWithOtherFieldProps {
  label: string;
  value?: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  otherOptionValue?: string;
  otherLabel?: string;
  otherValue: string;
  otherPlaceholder?: string;
  required?: boolean;
  onChange: (value: string) => void;
  onOtherChange: (value: string) => void;
}

/** Alegere unică (radio) + text liber „Care…?” doar la „Altul”. */
export const ChoiceWithOtherField = ({
  label,
  value,
  options,
  otherOptionValue = OTHER_OPTION_VALUE,
  otherLabel = 'Care altul',
  otherValue,
  otherPlaceholder,
  required,
  onChange,
  onOtherChange,
}: ChoiceWithOtherFieldProps) => (
  <Form.Item label={label} required={required} style={{ marginBottom: 16 }}>
    <Radio.Group
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
        if (e.target.value !== otherOptionValue && otherValue) onOtherChange('');
      }}
      options={options.map((o) => ({ value: o.value, label: o.label }))}
      style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
    />
    {value === otherOptionValue && (
      <Form.Item label={otherLabel} style={{ marginBottom: 0, marginTop: 8, marginLeft: 12 }}>
        <Input.TextArea
          rows={2}
          value={otherValue}
          placeholder={otherPlaceholder}
          onChange={(e) => onOtherChange(e.target.value)}
        />
      </Form.Item>
    )}
  </Form.Item>
);

interface YesNoChoiceWithOtherFieldProps {
  label: string;
  value?: YesNo;
  choiceValue?: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  choiceLabel?: string;
  otherOptionValue?: string;
  otherLabel?: string;
  otherValue: string;
  otherPlaceholder?: string;
  required?: boolean;
  onChange: (value: YesNo) => void;
  onChoiceChange: (value: string) => void;
  onOtherChange: (value: string) => void;
}

/**
 * Pattern complet pe alegere unică: Da/Nu → la Da listă radio → „Altul” deschide textul liber.
 * La Nu se ascund și se golesc alegerea + textul liber.
 */
export const YesNoChoiceWithOtherField = ({
  label,
  value,
  choiceValue,
  options,
  choiceLabel = 'Dacă da, specificați',
  otherOptionValue = OTHER_OPTION_VALUE,
  otherLabel = 'Care altul',
  otherValue,
  otherPlaceholder,
  required,
  onChange,
  onChoiceChange,
  onOtherChange,
}: YesNoChoiceWithOtherFieldProps) => (
  <div className="anamnesis-q" data-testid={`anamnesis-q-${label.slice(0, 40)}`}>
    <Form.Item label={label} required={required} style={{ marginBottom: 8 }}>
      <Radio.Group
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (e.target.value !== 'yes') {
            if (choiceValue) onChoiceChange('');
            if (otherValue) onOtherChange('');
          }
        }}
      >
        <Radio value="yes">Da</Radio>
        <Radio value="no">Nu</Radio>
      </Radio.Group>
    </Form.Item>
    {value === 'yes' && (
      <ChoiceWithOtherField
        label={choiceLabel}
        value={choiceValue}
        options={options}
        otherOptionValue={otherOptionValue}
        otherLabel={otherLabel}
        otherValue={otherValue}
        otherPlaceholder={otherPlaceholder}
        onChange={onChoiceChange}
        onOtherChange={onOtherChange}
      />
    )}
  </div>
);

interface TextInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  required?: boolean;
}

export const DetailTextInput = ({
  label,
  value,
  onChange,
  placeholder,
  rows = 2,
  disabled,
  required,
}: TextInputProps) => (
  <Form.Item label={label} required={required} style={{ marginBottom: 16 }}>
    <Input.TextArea
      rows={rows}
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  </Form.Item>
);

export const SectionHint = ({ children }: { children: ReactNode }) => (
  <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
    {children}
  </Text>
);
